import React, { useMemo, useState } from 'react';
import { createUseGesture, dragAction } from '@use-gesture/react';
import { useAppContext, useComposition, useFrami, useImages, useNavigation, useWorkspace } from '../../context';
import { updateCrop } from '../../utilities/composition';
import { uiColors } from '../../common/styles';
import { getPassepartoutBestFit } from '../../utilities/frame';

export const dist = (p1, p2) => {
    return Math.sqrt(Math.pow(p2[0] - p1[0], 2) + Math.pow(p2[1] - p1[1], 2));
};

const FrameTransformationHandler = ({
    position,
    frame,
    setPositionX,
    setPositionY,
    width,
    height,
    handlerType,
    shadowRef,
    faceWidth,
}) => {
    const useGesture = createUseGesture([dragAction]);
    const [handlerColor, setHandlerColor] = useState(uiColors.PRIMARY);
    const {
        cameraRef,
        checkFrameCollisions,
        setIsGesture,
        isDragging,
        isMultiTouching,
        isTransforming,
        screenToWorld,
        updateEditedFrames,
    } = useWorkspace();
    const { images } = useImages();
    const { isMobile } = useAppContext();
    const {
        currentFrami,
        getMinFrameSizeHeight,
        getMinFrameSizeWidth,
        getMaxFrameSizeHeight,
        getMaxFrameSizeWidth,
        memoizedFrameSizes,
    } = useFrami();
    const { activeFrames } = useComposition();
    const { navbarHeight } = useNavigation();
    const minFrameSizeWidth = useMemo(() => getMinFrameSizeWidth() / 1000, [getMinFrameSizeWidth]);
    const minFrameSizeHeight = useMemo(() => getMinFrameSizeHeight() / 1000, [getMinFrameSizeHeight]);
    const maxFrameSizeWidth = useMemo(() => getMaxFrameSizeWidth() / 1000, [getMaxFrameSizeWidth]);
    const maxFrameSizeHeight = useMemo(() => getMaxFrameSizeHeight() / 1000, [getMaxFrameSizeHeight]);

    const bind = useGesture(
        {
            onDrag: ({ movement: [dx, dy], xy: [x, y], cancel, down, event, first, tap, memo }) => {
                if (isMultiTouching.current || isDragging.current) {
                    cancel();
                    return;
                }
                if (tap) {
                    return;
                }
                event.stopPropagation();
                isTransforming.current = true;
                setIsGesture(true);
                if (first) {
                    memo = [
                        shadowRef.current.position.x,
                        shadowRef.current.position.y,
                        frame.frame_size,
                        width,
                        height,
                        frame.frame_left,
                        frame.frame_top,
                        frame.y,
                    ];
                }
                const [worldX, worldY] = screenToWorld(x, y, cameraRef.current, 0);
                const [orgWorldX, orgWorldY] = screenToWorld(x - dx, y - dy, cameraRef.current, 0);

                const newWidth = handlerType.endsWith('Right')
                    ? memo[3] + worldX - orgWorldX
                    : memo[3] - worldX + orgWorldX;
                const newHeight = handlerType.startsWith('top')
                    ? memo[4] + worldY - orgWorldY
                    : memo[4] - worldY + orgWorldY;

                const closestFrame = [...memoizedFrameSizes.values()].reduce((prev, curr) => {
                    const distCurr = dist([newWidth, newHeight], [curr.width / 1000, curr.height / 1000]);
                    const distPrev = dist([newWidth, newHeight], [prev.width / 1000, prev.height / 1000]);
                    return distCurr < distPrev ? curr : prev;
                });

                const distX = closestFrame.width / 1000 - memo[3];
                const distY = closestFrame.height / 1000 - memo[4];

                const newX = handlerType.endsWith('Right') ? memo[0] + distX / 2 : memo[0] - distX / 2;
                const newY = handlerType.startsWith('top') ? memo[1] + distY / 2 : memo[1] - distY / 2;

                const collision = checkFrameCollisions({
                    id: frame.id,
                    x: newX - closestFrame.width / 1000 / 2,
                    y: newY - closestFrame.height / 1000 / 2,
                    width: closestFrame.width / 1000,
                    height: closestFrame.height / 1000,
                });

                if (!collision) {
                    if (
                        newWidth < minFrameSizeWidth ||
                        newHeight < minFrameSizeHeight ||
                        newWidth > maxFrameSizeWidth ||
                        newHeight > maxFrameSizeHeight
                    ) {
                        setHandlerColor(uiColors.ERROR);
                    } else {
                        setHandlerColor(uiColors.PRIMARY);
                    }
                    let frameLeft = handlerType.endsWith('Right')
                        ? frame.frame_left
                        : Math.round(memo[5] - 1000 * distX);
                    let frameTop = handlerType.startsWith('bottom')
                        ? frame.frame_top
                        : Math.round(memo[6] - 1000 * distY);
                    let frameY = handlerType.startsWith('bottom') ? frame.y : Math.round(memo[7] + 1000 * distY);

                    frame.frame_size = closestFrame.id;
                    frame.frame_left = frameLeft;
                    frame.frame_top = frameTop;
                    frame.x = frameLeft;
                    frame.y = frameY;

                    frame.calculated.frame_width = closestFrame.width;
                    frame.calculated.frame_height = closestFrame.height;
                    frame.calculated.image_width = closestFrame.width - 2 * faceWidth * 1000;
                    frame.calculated.image_height = closestFrame.height - 2 * faceWidth * 1000;
                    frame.passepartout_width_mm = getPassepartoutBestFit(
                        currentFrami?.default_passepartout_width,
                        frame.calculated.image_width,
                        frame.calculated.image_height
                    );
                    frame.cropping = updateCrop(
                        images.find((i) => i.filename === frame.image),
                        frame,
                        false
                    )

                    setPositionX(newX);
                    setPositionY(newY);
                } else {
                    setHandlerColor(uiColors.ERROR);
                }

                if (!down) {
                    isTransforming.current = false;
                    setIsGesture(false);
                    setHandlerColor(uiColors.PRIMARY);
                }

                return memo;
            },
            onDragEnd: ({ movement: [dx, dy], xy: [x, y], down, event }) => {
                const transformedFrame = {
                    ...frame,
                    cropping: updateCrop(
                        images.find((i) => i.filename === frame.image),
                        frame,
                        false
                    ),
                };
                let framesToUpdate = [transformedFrame];
                if (activeFrames) {
                    framesToUpdate = activeFrames.map((f) => {
                        if (f.id === transformedFrame.id) {
                            return transformedFrame;
                        }
                        return f;
                    });
                }
                updateEditedFrames(framesToUpdate);
                isTransforming.current = false;
                setIsGesture(false);
                setHandlerColor(uiColors.PRIMARY);
            },
        },
        {
            drag: { filterTaps: true },
        }
    );

    return (
        <group
            {...bind()}
            name={`handler-${handlerType}`}
            position={position}
            onPointerOver={() => {
                document.body.style.cursor =
                    handlerType === 'topRight' || handlerType === 'bottomLeft' ? 'nesw-resize' : 'nwse-resize';
            }}
            onPointerOut={() => (document.body.style.cursor = 'auto')}
        >
            {/*{isMobile && (*/}
            <mesh position={[0, 0, 0.001]}>
                <planeGeometry attach="geometry" args={isMobile ? [0.1, 0.1] : [0.04, 0.04]} />
                <meshBasicMaterial visible={false} />
            </mesh>
            {/*)}*/}
            <mesh>
                <boxGeometry attach="geometry" args={isMobile ? [0.04, 0.04, 0.001] : [0.02, 0.02, 0.001]} />
                <meshBasicMaterial color={handlerColor} visible={!isDragging.current} />
            </mesh>
        </group>
    );
};

export default FrameTransformationHandler;
